import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConfigContext } from '../components/shared/ConfigContext';
import axios from 'axios';
import { emit, EVENTS } from '../utils/eventBus';

export const CartContext = createContext();

// Cart API client functions
const getCart = async (backendUrl) => {
  const userId = localStorage.getItem('userId');
  const companyId = localStorage.getItem('companyId');

  return axios.get(`${backendUrl}/cart`, {
    headers: {
      'X-User-Id': userId,
      'X-Company-Id': companyId
    }
  });
};

const addToCart = async (backendUrl, productId, quantity) => {
  const userId = localStorage.getItem('userId');
  const companyId = localStorage.getItem('companyId');

  return axios.post(`${backendUrl}/cart/items`, {
    productId,
    quantity
  }, {
    headers: {
      'X-User-Id': userId,
      'X-Company-Id': companyId
    }
  });
};

const updateCartItemQuantity = async (backendUrl, productId, quantity) => {
  const userId = localStorage.getItem('userId');

  return axios.put(`${backendUrl}/cart/items/${productId}`, {
    quantity
  }, {
    headers: {
      'X-User-Id': userId
    }
  });
};

const removeFromCart = async (backendUrl, productId) => {
  const userId = localStorage.getItem('userId');

  return axios.delete(`${backendUrl}/cart/items/${productId}`, {
    headers: {
      'X-User-Id': userId
    }
  });
};

const clearCart = async (backendUrl) => {
  const userId = localStorage.getItem('userId');

  return axios.delete(`${backendUrl}/cart`, {
    headers: {
      'X-User-Id': userId
    }
  });
};

const updateCartMessage = async (backendUrl, message) => {
  const userId = localStorage.getItem('userId');

  return axios.put(`${backendUrl}/cart/message`, {
    message
  }, {
    headers: {
      'X-User-Id': userId
    }
  });
};

const checkoutCart = async (backendUrl) => {
  const userId = localStorage.getItem('userId');
  const companyId = localStorage.getItem('companyId');
  const username = localStorage.getItem('username');

  return axios.post(`${backendUrl}/cart/checkout`, {
    username
  }, {
    headers: {
      'X-User-Id': userId,
      'X-Company-Id': companyId
    }
  });
};

export const CartProvider = ({ children }) => {
  const config = useContext(ConfigContext);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load cart data when config is available
  useEffect(() => {
    if (config) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const fetchCart = async () => {
    if (!config) return;

    setLoading(true);
    try {
      const companyId = localStorage.getItem('companyId');
      const response = await getCart(config.backendUrl);
      const cartData = response.data;
      
      await enrichCartWithInventory(cartData, companyId);
      
      setCart(cartData);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to enrich cart items with available quantity from inventory
  const enrichCartWithInventory = async (cartData, companyId) => {
    if (!cartData?.items?.length || !companyId || !config) return;
    
    try {
      const params = new URLSearchParams({ companyId });
      const inventoryResponse = await axios.get(`${config.backendUrl}/inventory?${params}`);
      const inventory = inventoryResponse.data;
      
      // Enrich cart items with available quantity from inventory
      cartData.items = cartData.items.map(item => {
        const invItem = inventory.find(inv => inv.productId === item.productId);
        return {
          ...item,
          availableQuantity: invItem?.quantity || 0
        };
      });
    } catch (invErr) {
      console.warn('Failed to fetch inventory for cart items:', invErr);
      // Continue without available quantity info
    }
  };

  const addItem = async (productId, quantity) => {
    if (!config) return { success: false, error: 'Config not available' };

    try {
      const response = await addToCart(config.backendUrl, productId, quantity);
      const cartData = response.data;
      
      // Re-fetch inventory to update available quantities after cart change
      const companyId = localStorage.getItem('companyId');
      await enrichCartWithInventory(cartData, companyId);
      
      setCart(cartData);
      emit(EVENTS.CART_CHANGED, { action: 'add', productId, quantity });
      return { success: true, data: cartData };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to add item to cart';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const addItemsToCart = async (items) => {
    if (!config) return { success: false, error: 'Config not available' };

    setLoading(true);
    try {
      // Add items sequentially
      const results = [];
      for (const item of items) {
        try {
          const result = await addToCart(config.backendUrl, item.productId, item.quantity);
          results.push({ success: true, data: result.data });
        } catch (error) {
          results.push({
            success: false,
            error: error.response?.data?.error || 'Failed to add item'
          });
        }
      }

      // Refresh cart after all operations
      await fetchCart();
      emit(EVENTS.CART_CHANGED, { action: 'bulkAdd', items });

      return {
        success: true,
        results
      };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add items to cart');
      return { success: false, error: err.response?.data?.error || 'Failed to add items to cart' };
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!config) return { success: false, error: 'Config not available' };

    try {
      const response = await updateCartItemQuantity(config.backendUrl, productId, quantity);
      const cartData = response.data;
      
      // Re-fetch inventory to update available quantities after cart change
      const companyId = localStorage.getItem('companyId');
      await enrichCartWithInventory(cartData, companyId);
      
      setCart(cartData);
      emit(EVENTS.CART_CHANGED, { action: 'updateQty', productId, quantity });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update item quantity');
      return { success: false, error: err.response?.data?.error || 'Failed to update item quantity' };
    }
  };

  const removeItem = async (productId) => {
    if (!config) return { success: false, error: 'Config not available' };

    try {
      const response = await removeFromCart(config.backendUrl, productId);
      const cartData = response.data;
      
      // Re-fetch inventory to update available quantities after cart change
      const companyId = localStorage.getItem('companyId');
      await enrichCartWithInventory(cartData, companyId);
      
      setCart(cartData);
      emit(EVENTS.CART_CHANGED, { action: 'remove', productId });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove item from cart');
      return { success: false, error: err.response?.data?.error || 'Failed to remove item from cart' };
    }
  };

  const clear = async () => {
    if (!config) return { success: false, error: 'Config not available' };

    setLoading(true);
    try {
      const response = await clearCart(config.backendUrl);
      setCart(response.data);
      emit(EVENTS.CART_CHANGED, { action: 'clear' });
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear cart');
      return { success: false, error: err.response?.data?.error || 'Failed to clear cart' };
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = async (message) => {
    if (!config) return { success: false, error: 'Config not available' };

    setLoading(true);
    try {
      const response = await updateCartMessage(config.backendUrl, message);
      setCart(prev => ({ ...prev, message: response.data.cartMessage }));
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update cart message');
      return { success: false, error: err.response?.data?.error || 'Failed to update cart message' };
    } finally {
      setLoading(false);
    }
  };

  const checkout = async () => {
    if (!config) return { success: false, error: 'Config not available' };

    setLoading(true);
    try {
      const response = await checkoutCart(config.backendUrl);
      setCart(null); // Clear local cart state after checkout
      await fetchCart(); // Refresh cart from server
      emit(EVENTS.ORDER_PLACED, { orders: response.data.orders });
      return { success: true, orders: response.data.orders };
    } catch (err) {
      setError(err.response?.data?.error || 'Checkout failed');
      return { success: false, error: err.response?.data?.error || 'Checkout failed' };
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        fetchCart,
        addToCart: addItem,
        addItemsToCart,
        updateQuantity,
        removeItem,
        clearCart: clear,
        updateMessage,
        checkout
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);